if (!customElements.get('description-tabs')) {
  customElements.define(
    'description-tabs',
    class DescriptionTabs extends HTMLElement {
      // Applies catalog-wide to every product sharing the standard description template.
      static HIDDEN_TABS = ['teavante promise', 'perfect for'];
      static RENAME_TABS = {
        'what it does for you': 'Natural Wellness Benefits',
      };
      static MERGE_TABS = {
        description: { into: 'about this tea', label: 'About This Tea', position: 'start' },
        'liquor profile': { into: 'tea details', position: 'end' },
      };

      connectedCallback() {
        this.source = this.querySelector('[data-description-source]');
        if (!this.source) return;

        const groups = this.applyTabRules(this.groupBySections(this.source));
        if (groups.length < 2) return;

        this.buildTabs(groups);
      }

      applyTabRules(tabs) {
        const normalize = (label) => label.trim().toLowerCase();
        const visible = tabs.filter((tab) => !DescriptionTabs.HIDDEN_TABS.includes(normalize(tab.label)));

        visible.forEach((tab) => {
          const renamed = DescriptionTabs.RENAME_TABS[normalize(tab.label)];
          if (renamed) tab.label = renamed;
        });

        return visible.filter((tab) => {
          const rule = DescriptionTabs.MERGE_TABS[normalize(tab.label)];
          if (!rule) return true;

          const target = visible.find((candidate) => candidate !== tab && normalize(candidate.label) === rule.into);
          if (target) {
            if (rule.position === 'start') target.nodes.unshift(...tab.nodes);
            else target.nodes.push(...tab.nodes);
            return false;
          }

          // No existing tab to merge into: this chunk becomes that tab itself instead of being dropped.
          if (rule.label) tab.label = rule.label;
          return true;
        });
      }

      groupBySections(source) {
        const HEADING_TAGS = ['H1', 'H2', 'H3'];
        const groups = [{ heading: null, nodes: [] }];

        Array.from(source.childNodes).forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'HR') {
            groups.push({ heading: null, nodes: [] });
            return;
          }

          const current = groups[groups.length - 1];
          const isFirstGroup = groups.length === 1;

          if (
            !isFirstGroup &&
            !current.heading &&
            node.nodeType === Node.ELEMENT_NODE &&
            HEADING_TAGS.includes(node.tagName) &&
            node.textContent.trim() !== ''
          ) {
            current.heading = node.textContent.trim();
            return;
          }

          if (isFirstGroup && node.nodeType === Node.ELEMENT_NODE && HEADING_TAGS.includes(node.tagName)) {
            return;
          }

          current.nodes.push(node);
        });

        const tabs = [];
        groups.forEach((group, index) => {
          const hasContent = group.nodes.some((node) => node.textContent && node.textContent.trim() !== '');
          if (index === 0) {
            if (hasContent) tabs.push({ label: this.dataset.descriptionLabel || 'Description', nodes: group.nodes });
            return;
          }
          if (group.heading) {
            tabs.push({ label: group.heading, nodes: group.nodes });
          } else if (hasContent && tabs.length) {
            // No heading found for this chunk: fold it into the previous tab rather than dropping it.
            tabs[tabs.length - 1].nodes.push(...group.nodes);
          }
        });

        return tabs;
      }

      buildTabs(groups) {
        const tabList = document.createElement('div');
        tabList.className = 'description-tabs__list';
        tabList.setAttribute('role', 'tablist');

        const panelWrap = document.createElement('div');
        panelWrap.className = 'description-tabs__panels';

        groups.forEach((group, index) => {
          const tabId = `${this.id}-tab-${index}`;
          const panelId = `${this.id}-panel-${index}`;

          const tabButton = document.createElement('button');
          tabButton.type = 'button';
          tabButton.className = 'description-tabs__tab';
          tabButton.id = tabId;
          tabButton.setAttribute('role', 'tab');
          tabButton.setAttribute('aria-controls', panelId);
          tabButton.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
          tabButton.textContent = group.label;
          tabButton.addEventListener('click', () => this.selectTab(index));
          tabList.appendChild(tabButton);

          const panel = document.createElement('div');
          panel.className = 'description-tabs__panel rte';
          panel.id = panelId;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', tabId);
          if (index !== 0) panel.hidden = true;
          group.nodes.forEach((node) => panel.appendChild(node.cloneNode(true)));
          panelWrap.appendChild(panel);
        });

        this.source.remove();
        this.append(tabList, panelWrap);
        this.tabs = Array.from(tabList.children);
        this.panels = Array.from(panelWrap.children);
      }

      selectTab(index) {
        this.tabs.forEach((tab, i) => tab.setAttribute('aria-selected', i === index ? 'true' : 'false'));
        this.panels.forEach((panel, i) => {
          panel.hidden = i !== index;
        });
        this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
      }
    }
  );
}
